import {
    SurveyConditionOperator,
    SurveyConditionSubject,
    SurveyQuestion,
    SurveyQuestionVisibilityRule
} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import {SurveyAnswer} from "crqlar-react-common/dist/models/survey/surveySubmission.model";

type SurveyVisibilityAnswer = Pick<
    SurveyAnswer,
    'scaleValue' | 'rateValue' | 'numberValue' | 'booleanValue' | 'optionIds'
>;

type SurveyVisibilityAnswers = Record<string, SurveyVisibilityAnswer | undefined>;

const isQuestionVisible = (
    question: SurveyQuestion,
    answersByQuestionId: SurveyVisibilityAnswers,
    matchedGuestCategoryIds?: string[]
): boolean => {
    const rules = question.visibilityRules;
    if (!rules?.length) return true;

    return rules.every(rule => passes(rule, answersByQuestionId, matchedGuestCategoryIds));
};

export const visibleSurveyQuestions = (
    questions: SurveyQuestion[],
    answersByQuestionId: SurveyVisibilityAnswers,
    matchedGuestCategoryIds?: string[]
): SurveyQuestion[] => (
    questions.filter(question => isQuestionVisible(question, answersByQuestionId, matchedGuestCategoryIds))
);

const passes = (
    rule: SurveyQuestionVisibilityRule,
    answersByQuestionId: SurveyVisibilityAnswers,
    matchedGuestCategoryIds?: string[]
): boolean => {
    const subject = rule.subject ?? SurveyConditionSubject.ANSWER;

    if (subject === SurveyConditionSubject.GUEST_CATEGORY) {
        return passesGuestCategory(rule, matchedGuestCategoryIds);
    }

    const answer = rule.questionId ? answersByQuestionId[rule.questionId] : undefined;
    if (!answer) return rule.operator === SurveyConditionOperator.NOT_EQUALS;

    return passesAnswer(rule, answer);
};

const passesGuestCategory = (
    rule: SurveyQuestionVisibilityRule,
    matchedGuestCategoryIds?: string[]
): boolean => {
    const expected = rule.guestCategoryId;
    // No snapshot at all (anonymous link) means the guest was never classified, so nothing can be judged.
    if (!expected || !matchedGuestCategoryIds) return false;

    switch (rule.operator) {
        case SurveyConditionOperator.EQUALS:
            return matchedGuestCategoryIds.includes(expected);
        case SurveyConditionOperator.NOT_EQUALS:
            return !matchedGuestCategoryIds.includes(expected);
        default:
            return false;
    }
};

const passesAnswer = (rule: SurveyQuestionVisibilityRule, answer: SurveyVisibilityAnswer): boolean => {
    const answerNumber = answer.scaleValue ?? answer.rateValue ?? answer.numberValue;
    const answerOptionIds = answer.optionIds ?? [];
    const ruleOptionIds = rule.optionIds ?? [];

    switch (rule.operator) {
        case SurveyConditionOperator.GTE:
            return isSet(rule.numberValue) && isSet(answerNumber) && answerNumber >= rule.numberValue;

        case SurveyConditionOperator.LTE:
            return isSet(rule.numberValue) && isSet(answerNumber) && answerNumber <= rule.numberValue;

        case SurveyConditionOperator.INCLUDES_ANY:
            return ruleOptionIds.some(optionId => answerOptionIds.includes(optionId));

        case SurveyConditionOperator.EQUALS:
            return matchesValue(rule, answer, answerNumber, answerOptionIds, ruleOptionIds);

        case SurveyConditionOperator.NOT_EQUALS:
            if (ruleOptionIds.length) {
                return !ruleOptionIds.some(optionId => answerOptionIds.includes(optionId));
            }
            return !matchesValue(rule, answer, answerNumber, answerOptionIds, ruleOptionIds);

        default:
            return false;
    }
};

const matchesValue = (
    rule: SurveyQuestionVisibilityRule,
    answer: SurveyVisibilityAnswer,
    answerNumber: number | undefined,
    answerOptionIds: string[],
    ruleOptionIds: string[]
): boolean => {
    if (isSet(rule.booleanValue)) return answer.booleanValue === rule.booleanValue;
    if (ruleOptionIds.length) return ruleOptionIds.every(optionId => answerOptionIds.includes(optionId));
    if (isSet(rule.numberValue)) return answerNumber === rule.numberValue;
    return false;
};

const isSet = <T>(value: T | undefined | null): value is T => value !== undefined && value !== null;
